import React, { useState, useEffect } from 'react';
import { collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp, 
  orderBy,
  limit,
  Timestamp } from '../firebase';
import { db, auth } from '../firebase';
import { 
  Clock, 
  LogIn, 
  LogOut, 
  MapPin, 
  Monitor, 
  Globe, 
  Calendar,
  CheckCircle2,
  AlertCircle,
  Loader2,
  History,
  ArrowRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { notify } from '../services/notify';

interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeEmail: string;
  employeeName: string;
  checkInTime: any;
  checkOutTime?: any;
  duration?: number;
  checkInMetadata: {
    deviceId: string;
    ip: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  checkOutMetadata?: {
    deviceId: string;
    ip: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  orgId: string;
  status: 'Checked In' | 'Completed';
}

interface AttendanceViewProps {
  orgId: string;
  theme: any;
  language: 'en' | 'ar';
}

const getLocalAuthToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;

const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

const getLocalUser = (token: string) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
};

const normalizeAttendanceRecord = (attendance: any): AttendanceRecord => {
  const metadata = attendance.metadata || {};
  const checkOutTime = attendance.checkOutTime ?? attendance.checkOut ?? attendance.check_out;

  return {
    id: attendance.id,
    employeeId: attendance.employeeId ?? attendance.employee_id ?? '',
    employeeEmail: attendance.employeeEmail ?? metadata.employeeEmail ?? '',
    employeeName: attendance.employeeName ?? metadata.employeeName ?? 'Employee',
    checkInTime: attendance.checkInTime ?? attendance.checkIn ?? attendance.check_in,
    ...(checkOutTime ? { checkOutTime } : {}),
    ...((attendance.duration ?? metadata.duration) !== undefined
      ? { duration: attendance.duration ?? metadata.duration }
      : {}),
    checkInMetadata: attendance.checkInMetadata ?? metadata.checkInMetadata ?? {
      deviceId: '',
      ip: 'Unknown',
    },
    ...(attendance.checkOutMetadata ?? metadata.checkOutMetadata
      ? { checkOutMetadata: attendance.checkOutMetadata ?? metadata.checkOutMetadata }
      : {}),
    orgId: attendance.orgId ?? attendance.org_id ?? '',
    status: attendance.status === 'Completed' || checkOutTime ? 'Completed' : 'Checked In',
  };
};

export default function AttendanceView({ orgId, theme: propTheme, language = 'en' }: AttendanceViewProps) {
  const theme = propTheme || {
    bg: "bg-background",
    text: "text-foreground",
    border: "border-border",
    sidebar: "bg-background",
    header: "bg-background/80",
    card: "bg-card",
    accent: "bg-primary",
    accentText: "text-primary-foreground",
    font: "font-sans",
    rounded: "rounded-2xl"
  };
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [activeRecord, setActiveRecord] = useState<AttendanceRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      const localAuthToken = getLocalAuthToken();
      if (!localAuthToken) {
        setIsLoading(false);
        return;
      }

      let cancelled = false;
      const localUser = getLocalUser(localAuthToken);
      const localEmployeeId = localUser?.sub || localUser?.uid;
      setIsLoading(true);

      const fetchLocalAttendance = async () => {
        try {
          const payload = await localApiRequest<any[]>(
            `/api/organizations/${encodeURIComponent(orgId)}/attendances`
          );
          if (cancelled) return;

          const attendanceData = (Array.isArray(payload) ? payload : [])
            .map(normalizeAttendanceRecord)
            .filter(record => !localEmployeeId || record.employeeId === localEmployeeId);
          setRecords(attendanceData);
          setActiveRecord(attendanceData.find(record => record.status === 'Checked In') || null);
        } catch (error) {
          if (!cancelled) console.error('Error fetching local attendance:', error);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      void fetchLocalAttendance();
      return () => { cancelled = true; };
    }

    if (!auth.currentUser) return;

    const q = query(
      collection(db, 'attendance'),
      where('orgId', '==', orgId),
      where('employeeId', '==', auth.currentUser.uid),
      orderBy('checkInTime', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const attendanceData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AttendanceRecord[];
      
      setRecords(attendanceData);
      
      // Find active check-in
      const active = attendanceData.find(r => r.status === 'Checked In');
      setActiveRecord(active || null);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching attendance:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [orgId]);

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('attendance_device_id');
    if (!deviceId) {
      deviceId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('attendance_device_id', deviceId);
    }
    return deviceId;
  };

  const getIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'Unknown';
    } catch (error) {
      console.error("Error fetching IP:", error);
      return 'Unknown';
    }
  };

  const getLocation = (): Promise<{ latitude: number; longitude: number } | undefined> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(undefined);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          resolve(undefined);
        },
        { timeout: 10000 }
      );
    });
  };

  const handleCheckIn = async () => {
    if (!orgId) return;
    const localSession = isLocalSession();
    if (!localSession && !auth.currentUser) return;
    setIsActionLoading(true);

    try {
      const [ip, location] = await Promise.all([
        getIpAddress(),
        getLocation()
      ]);

      if (localSession) {
        const localAuthToken = getLocalAuthToken();
        if (!localAuthToken) throw new Error('Local session token is unavailable');

        const localUser = getLocalUser(localAuthToken);
        const employeeId = localUser?.sub || localUser?.uid;
        if (!employeeId) throw new Error('Local session user is unavailable');

        const payload = await localApiRequest<any>(
          `/api/organizations/${encodeURIComponent(orgId)}/attendances`,
          {
            method: 'POST',
            body: JSON.stringify({
              employeeId,
              employeeEmail: localUser.email || 'No Email',
              employeeName: localUser.name || localUser.email || 'Employee',
              checkIn: new Date().toISOString(),
              checkInMetadata: { deviceId: getDeviceId(), ip, location: location || null },
              orgId,
              status: 'Checked In',
            }),
          }
        );

        const record = normalizeAttendanceRecord(payload);
        setRecords(currentRecords => [record, ...currentRecords]);
        setActiveRecord(record);
        notify.confirm('Successfully checked in!');
        return;
      }

      const checkInData = {
        employeeId: auth.currentUser.uid,
        employeeEmail: auth.currentUser.email || 'No Email',
        employeeName: auth.currentUser.displayName || 'Employee',
        checkInTime: serverTimestamp(),
        checkInMetadata: {
          deviceId: getDeviceId(),
          ip,
          location: location || null
        },
        orgId,
        status: 'Checked In' as const,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'attendance'), checkInData);
      notify.confirm("Successfully checked in!");
    } catch (error) {
      console.error("Error checking in:", error);
      notify.error("Failed to check in. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!activeRecord) return;
    const localSession = isLocalSession();
    setIsActionLoading(true);

    try {
      const [ip, location] = await Promise.all([
        getIpAddress(),
        getLocation()
      ]);

      const checkOutTime = new Date();
      const checkInTime = activeRecord.checkInTime instanceof Timestamp 
        ? activeRecord.checkInTime.toDate() 
        : new Date(activeRecord.checkInTime);
      
      const durationMs = checkOutTime.getTime() - checkInTime.getTime();
      const durationHours = durationMs / (1000 * 60 * 60);

      if (localSession) {
        const payload = await localApiRequest<any>(
          `/api/organizations/${encodeURIComponent(orgId)}/attendances/${encodeURIComponent(activeRecord.id)}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              checkOut: checkOutTime.toISOString(),
              checkOutMetadata: {
                deviceId: getDeviceId(),
                ip,
                location: location || null,
              },
              duration: parseFloat(durationHours.toFixed(2)),
              status: 'Completed',
            }),
          }
        );

        const record = normalizeAttendanceRecord(payload);
        setRecords(currentRecords => currentRecords.map(currentRecord =>
          currentRecord.id === record.id ? record : currentRecord
        ));
        setActiveRecord(null);
      } else {
        await updateDoc(doc(db, 'attendance', activeRecord.id), {
          checkOutTime: serverTimestamp(),
          checkOutMetadata: {
            deviceId: getDeviceId(),
            ip,
            location: location || null
          },
          duration: parseFloat(durationHours.toFixed(2)),
          status: 'Completed',
          updatedAt: serverTimestamp()
        });
      }

      notify.confirm("Successfully checked out!");
    } catch (error) {
      console.error("Error checking out:", error);
      notify.error("Failed to check out. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '--:--';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '--';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 opacity-50">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p className="text-xl font-bold italic serif">Loading attendance system...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className={cn("p-8 border shadow-sm", theme.card, theme.border, theme.rounded)}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-2 text-center md:text-left">
            <h2 className="text-4xl font-bold tracking-tight serif italic">Attendance Control</h2>
            <p className="text-sm opacity-50 font-bold uppercase tracking-widest">
              {currentTime.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="text-5xl font-mono font-bold tracking-tighter">
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Action Card */}
          <div className={cn(
            "p-8 border-2 transition-all duration-500 flex flex-col items-center justify-center text-center space-y-6",
            theme.rounded,
            activeRecord 
              ? "bg-green-50 border-green-200 text-green-900 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400" 
              : "bg-orange-50 border-orange-200 text-orange-900 dark:bg-orange-500/10 dark:border-orange-500/20 dark:text-orange-400"
          )}>
            <div className={cn(
              "w-20 h-20 rounded-full flex items-center justify-center shadow-lg",
              activeRecord ? "bg-green-500 text-white" : "bg-orange-500 text-white"
            )}>
              {activeRecord ? <CheckCircle2 className="w-10 h-10" /> : <Clock className="w-10 h-10" />}
            </div>
            
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">
                {activeRecord ? "You are currently Checked In" : "Ready to start your day?"}
              </h3>
              <p className="text-sm opacity-70 font-medium">
                {activeRecord 
                  ? `Started at ${formatTime(activeRecord.checkInTime)}` 
                  : "Please check in to record your working hours."}
              </p>
            </div>

            <button
              onClick={activeRecord ? handleCheckOut : handleCheckIn}
              disabled={isActionLoading}
              className={cn(
                "w-full max-w-xs py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50 shadow-lg",
                activeRecord 
                  ? "bg-red-600 text-white hover:bg-red-700 shadow-red-200 dark:shadow-none" 
                  : theme.accent + " " + theme.accentText + " shadow-gray-200 dark:shadow-none"
              )}
            >
              {isActionLoading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : activeRecord ? (
                <>
                  <LogOut className="w-6 h-6" />
                  Check Out Now
                </>
              ) : (
                <>
                  <LogIn className="w-6 h-6" />
                  Check In Now
                </>
              )}
            </button>
          </div>

          {/* Metadata Info Card */}
          <div className={cn("p-8 border space-y-6", theme.card, theme.border, theme.rounded)}>
            <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Current Session Info</h3>
            
            <div className="space-y-4">
              <div className={cn("flex items-center gap-4 p-4 rounded-2xl border", theme.bg, theme.border)}>
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-blue-600 border", theme.card, theme.border)}>
                  <Monitor className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold opacity-40">Device ID</p>
                  <p className="text-sm font-mono font-bold truncate max-w-[200px]">{getDeviceId()}</p>
                </div>
              </div>

              <div className={cn("flex items-center gap-4 p-4 rounded-2xl border", theme.bg, theme.border)}>
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-purple-600 border", theme.card, theme.border)}>
                  <Globe className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase font-bold opacity-40">Network IP</p>
                  <IpDisplay />
                </div>
              </div>

              <div className={cn("flex items-center gap-4 p-4 rounded-2xl border", theme.bg, theme.border)}>
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-orange-600 border", theme.card, theme.border)}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold opacity-40">Location Status</p>
                  <p className="text-sm font-bold">GPS Ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* History Section */}
      <div className={cn("border overflow-hidden shadow-sm", theme.card, theme.border, theme.rounded)}>
        <div className={cn("p-8 border-b flex items-center justify-between", theme.border)}>
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", theme.accent, theme.accentText)}>
              <History className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold serif italic">Attendance History</h3>
          </div>
          <p className="text-xs font-bold uppercase tracking-widest opacity-40">Last 50 Records</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className={cn("border-b", theme.bg, theme.border)}>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Date</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Check In</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Check Out</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Duration</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Metadata</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest opacity-40">Status</th>
              </tr>
            </thead>
            <tbody className={cn("divide-y", theme.border)}>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center justify-center opacity-30 space-y-4">
                      <Calendar className="w-12 h-12" />
                      <p className="text-lg font-bold">No attendance records found yet.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id} className="hover:bg-black/5 transition-colors group">
                    <td className="px-8 py-6">
                      <p className="font-bold text-sm">{formatDate(record.checkInTime)}</p>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2">
                        <LogIn className="w-3 h-3 text-green-500" />
                        <span className="font-mono font-bold">{formatTime(record.checkInTime)}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2">
                        <LogOut className="w-3 h-3 text-red-500" />
                        <span className="font-mono font-bold">{formatTime(record.checkOutTime)}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      {record.duration ? (
                        <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20">
                          {formatDuration(record.duration)}
                        </span>
                      ) : (
                        <span className="text-xs opacity-30 italic serif">In progress...</span>
                      )}
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="group/meta relative">
                          <button className={cn("p-2 border border-transparent rounded-lg transition-all", theme.bg, theme.border)}>
                            <MapPin className="w-4 h-4 opacity-40 group-hover:opacity-100" />
                          </button>
                          {record.checkInMetadata.location && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-[#141414] text-white text-[10px] rounded-xl opacity-0 invisible group-hover/meta:opacity-100 group-hover/meta:visible transition-all z-50">
                              <p className="font-bold uppercase tracking-widest mb-1 opacity-50">Check-in Location</p>
                              <p>Lat: {record.checkInMetadata.location.latitude.toFixed(4)}</p>
                              <p>Lng: {record.checkInMetadata.location.longitude.toFixed(4)}</p>
                            </div>
                          )}
                        </div>
                        <div className="group/meta relative">
                          <button className={cn("p-2 border border-transparent rounded-lg transition-all", theme.bg, theme.border)}>
                            <Globe className="w-4 h-4 opacity-40 group-hover:opacity-100" />
                          </button>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-[#141414] text-white text-[10px] rounded-xl opacity-0 invisible group-hover/meta:opacity-100 group-hover/meta:visible transition-all z-50">
                            <p className="font-bold uppercase tracking-widest mb-1 opacity-50">Network Details</p>
                            <p>IN: {record.checkInMetadata.ip}</p>
                            {record.checkOutMetadata && <p>OUT: {record.checkOutMetadata.ip}</p>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className={cn(
                        "px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border flex items-center w-fit gap-1.5",
                        record.status === 'Checked In' 
                          ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20" 
                          : "bg-gray-100 text-gray-700 border-gray-200 dark:bg-[#000000] dark:text-slate-400 dark:border-slate-700"
                      )}>
                        <div className={cn("w-1 h-1 rounded-full", record.status === 'Checked In' ? "bg-green-500" : "bg-gray-500")} />
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function IpDisplay() {
  const [ip, setIp] = useState<string>('Loading...');

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setIp(data.ip))
      .catch(() => setIp('Unavailable'));
  }, []);

  return <p className="text-sm font-bold">{ip}</p>;
}
